// "Answer here" on the Mission Panel card (#6726, epic #6552; prototype qaSend and
// qaNoteHTML, `docs/design/fleet-redesign/prototype.html` `:1453-1470`): one card's answer
// box and the note its answer leaves. One box open per card. Everything else is keyed by
// BLOCKER ID, never by row position or by "the box": the rows are an `<Index>`, and an
// answer still in flight on question A must not close, clear or mark question B's box when
// it settles.
//
// THE CARD OWNS THIS, NOT THE MISSION ZONE. Answering a card's only blocker empties its
// Mission zone on the re-read, and the note must outlive the row it answered, so the state
// lives with the card (`MissionCard`) and the zone renders while the note is up.
//
// THE CALL IS INJECTED. `send` is the page's (`useBlockerAnswer`, `lib/blockers.ts`): the
// same `answerBlocker` the Blockers page calls, then a re-read of the blockers query, so
// nothing here patches a row from the answer's echo. `createKeyedMutation` gives the
// per-blocker in-flight drop; the refusal is caught here and kept per blocker, in the
// Blockers page's words (`errorReason`, so a 422 `detail` wins). A 200 the reader cannot
// read (`BlockerAnswerUnreadable`) is an answer the server TOOK: it leaves a note saying so
// and logs the reader's message, never "That didn't work". The note's six seconds are
// `createToast`'s timer, the app's one.

import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { t } from '../i18n';
import { errorReason } from './api-errors';
import { type BlockerAnswered, BlockerAnswerUnreadable } from './blockers';
import { createKeyedMutation } from './keyed-mutation';
import { ANSWER_NOTE_MS, answerNote, canSendAnswer } from './machine-card';
import { createToast } from './toast';

/** Send one prose answer to one blocker; resolves with what the answer did. */
export type SendAnswer = (
  blockerId: string,
  reply: { readonly answer: string },
) => Promise<BlockerAnswered>;

export type CardAnswer = {
  /** The blocker whose box is open on this card, or null. */
  readonly openId: () => string | null;
  /** Open the box on one blocker, empty; any other box on the card closes. */
  readonly open: (blockerId: string) => void;
  /** Close the open box and keep nothing. */
  readonly close: () => void;
  readonly text: (blockerId: string) => string;
  readonly setText: (blockerId: string, text: string) => void;
  /** "Send answer" is pressable: two or more trimmed characters and nothing in flight. */
  readonly ready: (blockerId: string) => boolean;
  readonly sending: (blockerId: string) => boolean;
  /** This blocker's last refusal, in the Blockers page's words; its box and text stay. */
  readonly error: (blockerId: string) => string | null;
  /**
   * Send this blocker's text. Dropped below two characters and while its answer is in
   * flight; on success its box closes (if it is still the open one) and the note goes up.
   */
  readonly send: (blockerId: string) => Promise<void>;
  /** The note the last answer left, for {@link ANSWER_NOTE_MS}; null otherwise. */
  readonly note: () => string | null;
};

export type CardAnswerOptions = {
  /** Ran as the note expires, while it is still on screen (`createToast`'s `onExpire`). */
  readonly onNoteExpire?: () => void;
};

export function createCardAnswer(send: SendAnswer, options: CardAnswerOptions = {}): CardAnswer {
  const [openId, setOpenId] = createSignal<string | null>(null);
  const [texts, setTexts] = createStore<Record<string, string>>({});
  const [errors, setErrors] = createStore<Record<string, string | null>>({});
  const act = createKeyedMutation({ fallback: t('blockers.action_failed') });
  const note = createToast(ANSWER_NOTE_MS, options.onNoteExpire);

  const forget = (id: string): void => {
    setTexts(id, '');
    setErrors(id, null);
  };

  /** What one send ends in: the note to show, or null after a refusal (kept on its row). */
  const attempt = async (id: string, answer: string): Promise<string | null> => {
    try {
      return answerNote(await send(id, { answer }), t);
    } catch (err) {
      if (err instanceof BlockerAnswerUnreadable) {
        console.error(err.message);
        return t('fleet.machines.card_answer_unread');
      }
      setErrors(id, errorReason(err, t('blockers.action_failed')));
      return null;
    }
  };

  return {
    openId,
    open: (id) => {
      const was = openId();
      if (was !== null) forget(was);
      forget(id);
      setOpenId(id);
    },
    close: () => {
      const was = openId();
      if (was !== null) forget(was);
      setOpenId(null);
    },
    text: (id) => texts[id] ?? '',
    setText: (id, text) => setTexts(id, text),
    ready: (id) => canSendAnswer(texts[id] ?? '') && !act.isBusy(id),
    sending: act.isBusy,
    error: (id) => errors[id] ?? null,
    send: (id) => {
      const answer = (texts[id] ?? '').trim();
      if (!canSendAnswer(answer)) return Promise.resolve();
      return act.run(id, async () => {
        setErrors(id, null);
        const message = await attempt(id, answer);
        if (message === null) return;
        forget(id);
        if (openId() === id) setOpenId(null);
        note.notify(message);
      });
    },
    note: () => note.toast()?.message ?? null,
  };
}
